import { useMemo, useState } from 'react';
import { Plus, Search } from 'lucide-react';
import type { Client } from '@repo/api-client/dtos';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { ClientAvatar, inferClientTag } from '@/features/avatar';

interface ClientPickerDialogProps {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  clients: Client[];
  selectedId?: string;
  onPick: (client: Client) => void;
  /**
   * Required — clicking the inline "Add client" row fires this. The
   * caller decides what happens next (typically: open a quick-add
   * dialog and auto-select the new client on return).
   */
  onCreateNew: () => void;
}

export function ClientPickerDialog({
  open,
  onOpenChange,
  clients,
  selectedId,
  onPick,
  onCreateNew,
}: ClientPickerDialogProps) {
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (q === '') return clients;
    return clients.filter((c) => {
      const haystack = [
        c.name,
        c.displayName,
        c.company,
        clientEmail(c) ?? '',
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [clients, query]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Choose client</DialogTitle>
          <DialogDescription>Pick who this invoice is for.</DialogDescription>
        </DialogHeader>

        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search clients"
            className="pl-9"
            autoFocus
          />
        </div>

        <div className="flex max-h-[55vh] flex-col gap-1 overflow-y-auto">
          {/* Inline "Add client" row — same shape as ServicePickerDialog's
              "Create new service" row. Putting it in the footer next
              to Cancel made it read like a confirmation step. */}
          <button
            type="button"
            onClick={onCreateNew}
            className="flex items-center gap-3 rounded-lg border border-dashed border-line bg-secondary/20 px-3 py-2.5 text-left transition-colors hover:border-line-strong hover:bg-secondary/40"
          >
            <span className="flex size-8 items-center justify-center rounded-md bg-accent-soft/60 text-foreground">
              <Plus className="size-4" />
            </span>
            <span className="flex flex-col">
              <span className="text-sm font-medium text-foreground">
                Add client
              </span>
              <span className="text-[11px] text-muted-foreground">
                Just a name (and email if you have it) — fill the rest later
              </span>
            </span>
          </button>

          {filtered.length === 0 ? (
            <p className="px-2 py-6 text-center text-sm text-muted-foreground">
              {clients.length === 0
                ? 'No clients yet.'
                : `No clients match "${query}".`}
            </p>
          ) : (
            filtered.map((c) => {
              const email = clientEmail(c);
              const selected = c.id === selectedId;
              return (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => {
                    onPick(c);
                    onOpenChange(false);
                  }}
                  className={
                    'flex items-center justify-between gap-3 rounded-lg border px-3 py-2 text-left transition-colors ' +
                    (selected
                      ? 'border-accent/40 bg-accent-soft/40'
                      : 'border-transparent hover:border-line hover:bg-secondary/40')
                  }
                >
                  <div className="flex min-w-0 items-center gap-2.5">
                    <ClientAvatar
                      name={c.name}
                      photoUrl={c.photoUrl ?? null}
                      tag={inferClientTag({ name: c.name, company: c.company })}
                      size={32}
                    />
                    <div className="flex min-w-0 flex-col">
                      <span className="truncate text-sm font-medium text-foreground">
                        {c.displayName || c.name}
                      </span>
                      {email ? (
                        <span className="truncate text-xs text-muted-foreground">
                          {email}
                        </span>
                      ) : null}
                    </div>
                  </div>
                  {selected ? (
                    <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-accent-foreground">
                      Selected
                    </span>
                  ) : null}
                </button>
              );
            })
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function clientEmail(c: Client): string | null {
  const contact = c.contact;
  if (contact && typeof contact === 'object' && 'email' in contact) {
    const value = (contact as Record<string, unknown>).email;
    if (typeof value === 'string' && value.length > 0) return value;
  }
  return null;
}
